import { formatNumber } from "src/v2/utils/formatter/format-number"
import { formatDateString } from "src/utils/format-date"

const formatDate = (date) => {
    if (!date) return "-"
    return formatDateString(date, "-")
}

const hasValue = (value) => {
    return value !== null && value !== undefined && value !== ""
}

const hasAnyValue = (values) => {
    return values.some(hasValue)
}

const getValue = (value) => {
    return hasValue(value) ? value : "Não"
}

const formatNumberValue = (value, decimalPlaces = 4) => {
    if (!hasValue(value)) return "-"
    return formatNumber(value, decimalPlaces)
}

export const getSummarySections = ({
    garantiasData,
    offerData,
    fatosRelevantesData,
    handleClickEmission,
    stateRating,
    stateDebtorEmission,
    statePriceProcess,
    lastBusinessState,
    stateIndexLiquidity,
    lastEventCreditEmissionState,
    lastEventCreditDebtorState,

    isLoadingGarantias = false,
    isLoadingOffer = false,
    isLoadingFatosRelevantes = false,
}) => {
    const ultimoFatoRelevante = fatosRelevantesData?.[0]

    const lastRating = stateRating?.rows?.[0] || {}
    const lastDebtor = stateDebtorEmission?.rows?.[0] || {}
    const lastPrice = statePriceProcess?.rows?.[0] || {}
    const lastBusiness = lastBusinessState?.rows?.[0] || {}
    const lastIndex = stateIndexLiquidity?.rows?.[0] || {}
    const lastEventEmission = lastEventCreditEmissionState?.rows?.[0] || {}
    const lastEventDebtor = lastEventCreditDebtorState?.rows?.[0] || {}

    const isLoadingRating = Boolean(stateRating?.loading)
    const isLoadingDebtor = Boolean(stateDebtorEmission?.loading)
    const isLoadingPrice = Boolean(statePriceProcess?.loading)
    const isLoadingBusiness = Boolean(lastBusinessState?.loading)
    const isLoadingIndex = Boolean(stateIndexLiquidity?.loading)
    const isLoadingEventEmission = Boolean(lastEventCreditEmissionState?.loading)
    const isLoadingEventDebtor = Boolean(lastEventCreditDebtorState?.loading)

    const hasRating = Boolean(stateRating?.rows?.length)
    const hasDebtor = Boolean(stateDebtorEmission?.rows?.length)
    const hasPrice = Boolean(statePriceProcess?.rows?.length)
    const hasBusiness = Boolean(lastBusinessState?.rows?.length)
    const hasIndex = Boolean(stateIndexLiquidity?.rows?.length)
    const hasEventEmission = Boolean(lastEventCreditEmissionState?.rows?.length)
    const hasEventDebtor = Boolean(lastEventCreditDebtorState?.rows?.length)

    const hasGarantias =
        garantiasData?.GarantiasReais?.GarantiasReais === "1" ||
        garantiasData?.GarantiasFidejussorias?.GarantiasFidejussorias === "1" ||
        garantiasData?.Mitigadores?.MitigadorRiscoCredito === "1"

    const hasOffer = hasAnyValue([offerData?.TipoOferta])

    const hasFatoRelevante = hasAnyValue([
        ultimoFatoRelevante?.DataReferencia,
        ultimoFatoRelevante?.Fato,
    ])

    return [
        {
            title: "Último Rating Emissão",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingRating,
            empty: !isLoadingRating && !hasRating,
            emptyText: "Não há rating de emissão para o CETIP consultado",
            onClick: isLoadingRating
                ? null
                : () => handleClickEmission("last_emission"),
            fields: [
                {
                    label: "DATA VALIDADE",
                    value: formatDate(lastRating?.DataValidade),
                },
                {
                    label: "FONTE",
                    value: lastRating?.Fonte ?? "-",
                },
                {
                    label: "RATING",
                    value: lastRating?.Rating ?? "-",
                },
            ],
        },
        {
            title: "Último Rating Devedor",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingDebtor,
            empty: !isLoadingDebtor && !hasDebtor,
            emptyText: "Não há rating devedor para o CETIP consultado",
            onClick: isLoadingDebtor
                ? null
                : () => handleClickEmission("last_debtor"),
            fields: [
                {
                    label: "DATA VALIDADE",
                    value: formatDate(lastDebtor?.DataValidade),
                },
                {
                    label: "FONTE",
                    value: lastDebtor?.Fonte ?? "-",
                },
                {
                    label: "RATING",
                    value: lastDebtor?.Rating ?? "-",
                },
            ],
        },
        {
            title: "Garantias",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingGarantias,
            empty: !isLoadingGarantias && !hasGarantias,
            emptyText: "Não há garantias para o CETIP consultado",
            onClick: isLoadingGarantias
                ? null
                : () => handleClickEmission("guarantees"),
            fields: [
                {
                    label: "REAIS",
                    value:
                        garantiasData?.GarantiasReais?.GarantiasReais === "1"
                            ? "Sim"
                            : "Não",
                },
                {
                    label: "FIDEJUSSORIAS",
                    value:
                        garantiasData?.GarantiasFidejussorias
                            ?.GarantiasFidejussorias === "1"
                            ? "Sim"
                            : "Não",
                },
                {
                    label: "MITIGADORES RISCO CREDITO",
                    value:
                        garantiasData?.Mitigadores?.MitigadorRiscoCredito ===
                        "1"
                            ? "Sim"
                            : "Não",
                },
            ],
        },
        {
            title: "Última Taxa Indicativa",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingPrice,
            empty: !isLoadingPrice && !hasPrice,
            emptyText: "Não há taxa indicativa para o CETIP consultado",
            onClick: isLoadingPrice
                ? null
                : () => handleClickEmission("tax_indicative"),
            fields: [
                {
                    label: "DATA REFERENCIA",
                    value: formatDate(lastPrice?.DataReferencia),
                },
                {
                    label: "SPREAD CRÉDITO",
                    value: formatNumberValue(
                        lastPrice?.SpreadCredito ??
                            lastPrice?.SpreadCreditoFinal,
                        4
                    ),
                },
                {
                    label: "% PUPAR",
                    value: formatNumberValue(lastPrice?.PercentualPUPAR, 4),
                },
            ],
        },
        {
            title: "Últimos Negócios",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingBusiness,
            empty: !isLoadingBusiness && !hasBusiness,
            emptyText: "Não há negócios para o CETIP consultado",
            onClick: isLoadingBusiness
                ? null
                : () => handleClickEmission("history_last_negocitacions"),
            fields: [
                {
                    label: "DATA REFERENCIA",
                    value: formatDate(lastBusiness?.DataReferencia),
                },
                {
                    label: "% PUPAR",
                    value: formatNumberValue(lastBusiness?.PercentualPUPAR, 4),
                },
                {
                    label: "SPREAD CRÉDITO",
                    value: formatNumberValue(
                        lastBusiness?.SpreadCreditoFinal,
                        4
                    ),
                },
            ],
        },
        {
            title: "Último Índice de Liquidez",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingIndex,
            empty: !isLoadingIndex && !hasIndex,
            emptyText: "Não há índice de liquidez para o CETIP consultado",
            onClick: isLoadingIndex
                ? null
                : () => handleClickEmission("index_liquidity"),
            fields: [
                {
                    label: "DATA REFERENCIA",
                    value: formatDate(lastIndex?.DataReferencia),
                },
                {
                    label: "INDICE LIQUIDEZ",
                    value: formatNumberValue(lastIndex?.IndiceLiquidez, 4),
                },
            ],
        },
        {
            title: "Último Evento de Crédito Emissão",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingEventEmission,
            empty: !isLoadingEventEmission && !hasEventEmission,
            emptyText:
                "Não há evento de crédito de emissão para o CETIP consultado",
            onClick: isLoadingEventEmission
                ? null
                : () => handleClickEmission("history_event_credit_emission"),
            fields: [
                {
                    label: "DATA REFERENCIA",
                    value: formatDate(lastEventEmission?.DataReferencia),
                },
                {
                    label: "SCORE EVENTO",
                    value: formatNumberValue(lastEventEmission?.ScoreEvento, 4),
                },
                {
                    label: "EVENTO CRÉDITO",
                    value: lastEventEmission?.EventoCreditoEmissao ?? "-",
                    gridProps: { xxxs: 4, md: 8, lg: 8 },
                },
            ],
        },
        {
            title: "Último Evento de Crédito Devedor",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingEventDebtor,
            empty: !isLoadingEventDebtor && !hasEventDebtor,
            emptyText: "Não há evento de crédito devedor para o CETIP consultado",
            onClick: isLoadingEventDebtor
                ? null
                : () => handleClickEmission("history_event_credit_debtor"),
            fields: [
                {
                    label: "DATA REFERENCIA",
                    value: formatDate(lastEventDebtor?.DataReferencia),
                },
                {
                    label: "SCORE EVENTO",
                    value: formatNumberValue(lastEventDebtor?.ScoreEvento, 4),
                },
                {
                    label: "EVENTO CREDITO",
                    value: lastEventDebtor?.EventoCreditoDevedor ?? "-",
                    gridProps: { xxxs: 4, md: 8, lg: 8 },
                },
            ],
        },
        {
            title: "Tipo de Oferta",
            gridProps: { xxxs: 4, sm: 6, lg: 4 },
            loading: isLoadingOffer,
            empty: !isLoadingOffer && !hasOffer,
            emptyText: "Não há dados de oferta para o CETIP consultado",
            onClick: isLoadingOffer
                ? null
                : () => handleClickEmission("offer_type"),
            fields: [
                {
                    label: "TIPO",
                    value: `Oferta ${getValue(offerData?.TipoOferta)}`,
                },
            ],
        },
        {
            title: "Último Fato Relevante",
            gridProps: { xxxs: 4, sm: 12, lg: 12 },
            loading: isLoadingFatosRelevantes,
            empty: !isLoadingFatosRelevantes && !hasFatoRelevante,
            emptyText: "Não há fato relevante para o CETIP consultado",
            onClick: isLoadingFatosRelevantes
                ? null
                : () => handleClickEmission("relevant_facts_history"),
            fields: [
                {
                    label: "DATA REFERÊNCIA",
                    value: formatDate(ultimoFatoRelevante?.DataReferencia),
                    gridProps: { xxxs: 4, sm: 3, lg: 2 },
                },
                {
                    label: "FATO",
                    value: ultimoFatoRelevante?.Fato ?? "-",
                    gridProps: { xxxs: 4, sm: 9, lg: 10 },
                    valueSx: {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                    },
                },
            ],
        },
    ]
}